import { useCallback, useEffect, useRef, useState } from 'react';
import { pb } from './pb';
import type { PipelineEvent } from '../../../shared/types';

/**
 * The shared entity types describe our fields, not PocketBase's system fields,
 * so they don't satisfy `RecordModel`. Everything here only needs `id`, and the
 * SDK calls are cast at the boundary.
 */
type Identified = { id: string; expand?: unknown };

/**
 * Live data via PocketBase subscriptions.
 *
 * v1 ran four concurrent pollers: MAIN every 15s, USER DEBUG every 10s, and
 * both review tabs every 15s — each fetching a full sheet range (`MAIN!A:AZ`
 * spanned 1,045 rows to return 15 records) with no backoff. All of that is
 * replaced by these subscriptions: one fetch, then push updates.
 */

export interface CollectionState<T> {
  items: T[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

export function useCollection<T extends Identified>(
  collection: string,
  options: { filter?: string; sort?: string; expand?: string; enabled?: boolean } = {}
): CollectionState<T> {
  const { filter, sort, expand, enabled = true } = options;

  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  // Kept in a ref so the subscription effect doesn't re-run on every render.
  const queryRef = useRef({ filter, sort, expand });
  queryRef.current = { filter, sort, expand };

  const reload = useCallback(async () => {
    if (!enabled) return;
    try {
      const result = await pb.collection(collection).getFullList({
        ...(queryRef.current.filter ? { filter: queryRef.current.filter } : {}),
        ...(queryRef.current.sort ? { sort: queryRef.current.sort } : {}),
        ...(queryRef.current.expand ? { expand: queryRef.current.expand } : {}),
        batch: 200,
      });
      setItems(result as unknown as T[]);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [collection, enabled]);

  useEffect(() => {
    // A new query (different run selected, different filter) is a fresh load —
    // without this the previous query's rows render under the new header until
    // the fetch lands. Background realtime refetches don't touch `loading`.
    setLoading(enabled);
    void reload();
  }, [reload, filter, sort, enabled]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const subscribe = pb.collection(collection).subscribe('*', (event) => {
      if (cancelled) return;

      setItems((current) => {
        // A subscription delivers every change in the collection, including
        // records outside this view's filter. Refetching on create is simpler
        // and safer than trying to evaluate the filter client-side.
        if (event.action === 'create') {
          void reload();
          return current;
        }
        if (event.action === 'delete') {
          return current.filter((item) => item.id !== event.record.id);
        }

        // An update can move a record into or out of a filtered view (a job
        // changing status, a review row reset for a new round). The filter
        // can't be evaluated client-side, so filtered views refetch.
        if (queryRef.current.filter) {
          void reload();
          return current;
        }

        const index = current.findIndex((item) => item.id === event.record.id);
        if (index === -1) return current;

        const updated = [...current];
        // Preserve `expand`: realtime payloads don't carry expanded relations.
        updated[index] = {
          ...current[index],
          ...event.record,
          expand: current[index]?.expand,
        } as unknown as T;
        return updated;
      });
    });

    return () => {
      cancelled = true;
      void subscribe.then((unsub) => unsub()).catch(() => {});
    };
  }, [collection, enabled, reload]);

  return { items, loading, error, reload };
}

/** A single record, kept live. */
export function useRecord<T extends Identified>(
  collection: string,
  id: string | null,
  expand?: string
): { record: T | null; loading: boolean; error: string | null; reload: () => Promise<void> } {
  const [record, setRecord] = useState<T | null>(null);
  const [loading, setLoading] = useState(Boolean(id));
  const [error, setError] = useState<string | null>(null);

  // Reset synchronously when the id changes (the render-time adjustment
  // pattern), NOT in an effect: an effect leaves one commit where a caller
  // still sees the previous id's record/loading/error against the new id.
  // That stale window is exactly what bounced a freshly created campaign
  // back to the form.
  const [lastId, setLastId] = useState(id);
  if (id !== lastId) {
    setLastId(id);
    setRecord(null);
    setError(null);
    setLoading(Boolean(id));
  }

  const reload = useCallback(async () => {
    if (!id) {
      setRecord(null);
      setLoading(false);
      return;
    }
    try {
      const result = await pb
        .collection(collection)
        .getOne(id, expand ? { expand } : undefined);
      setRecord(result as unknown as T);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [collection, id, expand]);

  useEffect(() => {
    setLoading(Boolean(id));
    void reload();
  }, [reload, id]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const subscribe = pb.collection(collection).subscribe(id, (event) => {
      if (cancelled) return;
      if (event.action === 'delete') {
        setRecord(null);
        return;
      }
      // Refetch rather than merging: stage changes usually come with relation
      // changes the realtime payload doesn't include.
      void reload();
    });

    return () => {
      cancelled = true;
      void subscribe.then((unsub) => unsub()).catch(() => {});
    };
  }, [collection, id, reload]);

  return { record, loading, error, reload };
}

/**
 * A run's log, tailing live.
 *
 * Because events are written as each step happens rather than at the end, this
 * shows progress in real time and — the part n8n could not do — still shows
 * everything if the run is cancelled or the worker dies.
 */
export function useRunLog(runId: string | null): CollectionState<PipelineEvent> {
  return useCollection<PipelineEvent>('pipeline_events', {
    filter: runId ? `run = "${runId}"` : undefined,
    // Ordered by timestamp, not `seq`: each job in a run gets a fresh RunLogger
    // whose counter restarts at 1, so sorting by seq alone interleaves the
    // stages and makes new lines appear in the middle of the log.
    sort: 'created,seq',
    enabled: Boolean(runId),
  });
}

/** Debounced value, for autosaving review notes without a write per keystroke. */
export function useDebounced<T>(value: T, delayMs = 600): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/** Tracks whether the browser thinks it's online, to explain a dead subscription. */
export function useOnline(): boolean {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}
